import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import useLocationsService from '../../services/useLocationsService';
import showToast from '../../utils/toast';
import '../Services/Adicional/AdicionalCatalogModal.css';

export interface QuickCreatedLocation {
  id: number;
  name: string;
}

interface LocationQuickAddModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Se llama con la ubicación recién creada; el padre decide selección y cierre. */
  onCreated: (location: QuickCreatedLocation) => void;
}

/**
 * Helper para crear una ubicación del catálogo sin salir del formulario
 * de servicios (paso 2). Autocontenido: llama al servicio de locations.
 */
const LocationQuickAddModal: React.FC<LocationQuickAddModalProps> = ({
  isOpen,
  onClose,
  onCreated,
}) => {
  const { store, fetching } = useLocationsService();
  const [name, setName] = useState('');
  const loading = Boolean(fetching.store);

  useEffect(() => {
    if (isOpen) {
      setName('');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || loading) return;

    store(
      { name: trimmed },
      {
        onSuccess: (r: any) => {
          const id = r?.data?.id;
          if (typeof id !== 'number') {
            showToast('La ubicación se creó pero no se pudo seleccionar automáticamente', 'info');
            onClose();
            return;
          }
          showToast('Ubicación creada correctamente', 'success');
          onCreated({ id, name: trimmed });
        },
        onError: (r: any) => {
          const msg =
            typeof r?.message === 'string' && r.message.trim()
              ? r.message.trim()
              : 'No se pudo crear la ubicación';
          showToast(msg, 'error');
        },
      }
    );
  };

  if (!isOpen) return null;

  return (
    <div className="adicional-modal-overlay" onClick={onClose}>
      <div className="adicional-modal-content" onClick={e => e.stopPropagation()}>
        <div className="adicional-modal-header">
          <h3 className="adicional-modal-title">Nueva ubicación</h3>
          <button
            type="button"
            className="adicional-modal-close"
            onClick={onClose}
            aria-label="Cerrar"
          >
            <X size={22} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="adicional-modal-form">
          <div className="adicional-modal-field">
            <label>Nombre</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Ej. Aeropuerto El Loa"
              required
              autoFocus
              disabled={loading}
              maxLength={255}
            />
          </div>
          <div className="adicional-modal-actions">
            <button type="button" className="adicional-modal-btn secondary" onClick={onClose}>
              Cancelar
            </button>
            <button
              type="submit"
              className="adicional-modal-btn primary"
              disabled={loading || !name.trim()}
            >
              {loading ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LocationQuickAddModal;
